import {
  Box,
  Divider,
  FormControl,
  MenuItem,
  Select,
} from '@forgeglobal/design-framework';
import {
  FormHelperText,
  InputLabel,
  SelectProps as MuiSelectProps,
} from '@mui/material';
import { FieldRenderProps } from '@tcs/common/components/atoms/Form';
import cs from 'classnames';
import kebabCase from 'lodash/kebabCase';
import React, { ReactNode, useRef, useState } from 'react';

import { DropdownFieldActionProps } from './DropdownFieldAction';
import { useStyles } from './style';

export { default as DropdownFieldAction } from './DropdownFieldAction';

export interface SelectOption {
  label: string;
  value: string;
  action?: React.ReactElement<DropdownFieldActionProps>;
}

interface SelectProps
  extends FieldRenderProps<string>,
    Omit<MuiSelectProps, 'input' | 'variant' | 'type' | 'classes'> {
  label?: string;
  options: SelectOption[];
  renderAddon?: (updatePosition: () => void) => ReactNode;
  validateFirst?: boolean;
  helperText?: string;
}

const DropdownField = ({
  label,
  required = false,
  disabled = false,
  input,
  meta,
  options,
  renderAddon,
  validateFirst,
  helperText,
  sx,
  ...rest
}: SelectProps) => {
  const popoverRef = useRef(null);
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const { onChange, onBlur, ...inputProps } = input;

  const isInvalid = validateFirst ? meta.invalid : meta.touched && meta.invalid;
  const helper = isInvalid ? meta.error : helperText;

  const handleOnChange = ({ target }) => {
    if (target.value === undefined) {
      return;
    }
    setOpen(false);
    onChange(target && target.value);
  };

  const handleOnBlur = ({ target }) => {
    if (target.value === undefined) {
      return;
    }
    setOpen(false);
    onBlur(target && target.value);
  };

  const handleOnOpen = () => {
    setOpen(true);
  };

  const handleOnClose = () => {
    setOpen(false);
  };

  const updatePosition = () => {
    if (popoverRef.current) {
      popoverRef.current.updatePosition();
    }
  };

  const renderValue = (selected: string) => selected;

  return (
    <Box mb={1}>
      <FormControl
        component="fieldset"
        fullWidth
        classes={{ marginDense: classes.marginDense }}
        sx={sx}>
        {label && (
          <InputLabel
            required={required}
            error={isInvalid}
            className={
              !inputProps.value ? classes.labelWithoutValue : classes.label
            }
            variant="filled">
            {label}
          </InputLabel>
        )}
        <Select
          error={isInvalid}
          MenuProps={{
            action: popoverRef,
            onClose: handleOnClose,
          }}
          className={classes.select}
          renderValue={renderValue}
          open={open}
          onOpen={handleOnOpen}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          value={inputProps.value}
          onDelete={null}
          disabled={disabled}
          {...rest}>
          {options.map(
            ({ label, value, action }: SelectOption, index: number) => (
              <MenuItem
                key={`options-${index}-${kebabCase(value)}`}
                className={cs(classes.menuItem, {
                  [classes.menuItemAction]: action !== undefined,
                })}
                value={value}>
                <div className={classes.menuItemLabel} title={label}>
                  {label}
                </div>
                {action}
              </MenuItem>
            )
          )}
          {renderAddon && (
            <div>
              <Divider />
              {renderAddon(updatePosition)}
            </div>
          )}
        </Select>
        {helper && (
          <FormHelperText variant="filled" error={isInvalid}>
            {helper}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default DropdownField;
